import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import { useAdCampaigns } from "../../components/hooks/useAdCampaigns";
import {
  apiFetchSkitById,
  apiDeleteSkitIdFromSourceIdAction,
  apiReportSkit,
} from "../../api/backend/skits";
import {
  SmallPlainInsetBtn,
  MemberSlider,
  SolidColorButton,
} from "../../components/buttons/index";
import BallotModal from "../../components/ballotModal/ballotModal";
import {
  BallotIcon,
  WhistleIcon,
  TrashCanIcon,
  NotInterestedIcon,
  LeftArrow,
} from "../../components/icons/index";
import useSourceIdDeletePermission from "../../components/hooks/useSourceIdDeletePermission";
import VideoMonitor from "../../components/videoPlayer/videoMonitor";
import VideoPlayerControls from "../../components/videoPlayer/videoPlayerControls";
import {
  apiRecordSourceIDActivityView,
  apiFetchUserViewedSkitActivity,
  apiRecordSourceIDActivityRatings,
  apiRecordSourceIDActivityClap,
} from "../../api/backend/user";
import { cancelVoteTrasnaction } from "../../controller/cancelBallotModalTrasaction";
import CommentThreads from "../../components/memberComments/commentThreads";
import TooltipDots from "../../components/toolTipDots/toolTipDots";
import { scrollWithOffset } from "../../utils/scrollUtils";
import { SingularPlural } from "../../utils/wordUtils";
import TimeRemainingAd from "../../components/skitAd/timeRemainingAd";
import { saveSkitTime, getSkitTime } from "../../utils/skitAd";
import AdClickableLink from "../../components/adClickableLink/adClickableLink";
import {
  apiRecordAdClick,
  apiRecordAdImpression,
} from "../../api/backend/campaign";
import ReportModal from "../../components/reportModal/reportModal";
import HandClap from "../../components/handClap/handClap";
import "./skitsUrl.scss";

const SkitURL = ({ onNotInterested, autoPlay = true }) => {
  const {
    currentMemberId,
    playCount,
    setPlayCount,
    handleLogout,
    setSuccessFlashMessage,
  } = useAuth();
  const { fetchNotifications, notificationsCountFetchedFromMiddleware } =
    useNotifications();
  const { getNextAd } = useAdCampaigns(); // Hook for fetching next ad
  const [skit, setSkit] = useState(null);
  const [isAdPlaying, setIsAdPlaying] = useState(false); // State to track if an ad is playing
  const [isSkitPlaying, setIsSkitPlaying] = useState(autoPlay);
  const [adDuration, setAdDuration] = useState(null); // Ad duration will be updated dynamically
  const [skitDuration, setSkitDuration] = useState(skit?.duration || 1);
  const duration = isAdPlaying ? adDuration : skitDuration;
  const [currentMediaUrl, setCurrentMediaUrl] = useState(null); // Start with `null`
  const [adDetails, setAdDetails] = useState(null); // State to store current ad details
  const videoRef = useRef(null); // Reference to the video element
  const impressionRecordedRef = useRef(false);
  const navigate = useNavigate();
  const { skitId } = useParams();
  const [showReportModal, setShowReportModal] = useState(false);
  console.log("Skit Url Retrieved skitId:", skitId);
  console.log("At render, currentMemberId is", currentMemberId);
  const [volume, setVolume] = useState(5);
  const [views, setViews] = useState(skit?.views || 0);
  // With the nullish coalescing operator (??), if skit is null or undefined,
  // it will use 0 as the default, preserving the actual value if it’s 0.
  const [claps, setClaps] = useState(skit?.claps ?? 0);
  // Add state for claps
  const [clapsError, setClapsError] = useState("");
  const [rating, setRating] = useState(skit?.weightedAverageRating || 0);
  const [votes, setVotes] = useState(skit?.ratingCount || 0);
  const [viewError, setViewError] = useState("");
  const [ratingError, setRatingError] = useState("");
  const [showBallotModal, setShowBallotModal] = useState(false);
  const [notInterestedError, setNotInterestedError] = useState("");
  const savedTime = getSkitTime(skit?.id) || 0;
  const cancelVote = cancelVoteTrasnaction(setShowBallotModal);
  const handleNotInterestedClick = () => {
    onNotInterested(skit.id); // Pass the skit id to the handler
  };

  useEffect(() => {
    if (isAdPlaying && adDetails && !impressionRecordedRef.current) {
      // If campaignAdUrl is null, it's an impression-only ad.
      if (!adDetails.campaignAdUrl) {
        console.log("[IMPRESSION] Recording ad impression for ad:", {
          adId: adDetails.adId,
          sourceId: currentMemberId,
          targetId: adDetails.targetId,
          campaignName: adDetails.campaignName,
        });
        apiRecordAdImpression(
          adDetails.adId,
          currentMemberId,
          adDetails.targetId,
          adDetails.campaignName
        )
          .then((response) => {
            console.log(
              "[IMPRESSION] Ad impression recorded successfully:",
              response
            );
            impressionRecordedRef.current = true;
          })
          .catch((error) => {
            console.error("[IMPRESSION] Error recording ad impression:", error);
          });
      }
    }
  }, [isAdPlaying, adDetails, currentMemberId]);

  // Initialize hasDeletePermission here and update it only after skit is set
  // Move the declaration of useSourceDeletePermission hook outside of any function or condition
  useEffect(() => {
    if (!skitId) return;

    const fetchSkitDetails = async () => {
      try {
        const response = await apiFetchSkitById({ skitId });
        if (response.data) {
          setSkit(response.data);
          setViews(response.data.views);
          setVotes(response.data.ratingCount);
          setRating(response.data.weightedAverageRating);
          setSkitDuration(response.data.duration || 1); // Set skit duration
          setCurrentMediaUrl(response.data.mediaUrl); // Set media URL for the skit
          setClaps(response.data.claps || 0);
        }
      } catch (error) {
        console.error("Failed to fetch skit details:", error);
      }
    };

    fetchSkitDetails();
  }, [skitId]);
  useEffect(() => {
    const video = videoRef.current;

    if (video && autoPlay) {
      const playVideo = async () => {
        try {
          await video.play();
          setIsSkitPlaying(true);
          console.log("Skit is playing...");
        } catch (error) {
          console.error("Error playing skit:", error);
          setIsSkitPlaying(false);
        }
      };

      playVideo();
    }
  }, [currentMediaUrl, autoPlay]);

  const hasDeletePermission = useSourceIdDeletePermission(
    currentMemberId,
    skit?.sourceId // Use optional chaining to avoid null reference errors
  );
  const owner = hasDeletePermission;
  const denyOtherPermissions = owner;

  // Fetch skit details on mount or when skitId changes
  useEffect(() => {
    const fetchSkitDetails = async () => {
      if (!skitId) return;
      try {
        const response = await apiFetchSkitById({ skitId });
        if (response.data) {
          setSkit(response.data);
          setViews(response.data.views);
          setVotes(response.data.ratingCount);
          setRating(response.data.weightedAverageRating);
          setSkitDuration(response.data.duration || 1);
          setCurrentMediaUrl(response.data.mediaUrl);
        }
      } catch (error) {
        console.error("Failed to fetch skit details:", error);
      }
    };

    fetchSkitDetails();
  }, [skitId]);

  // Handle ad end logic
  useEffect(() => {
    const video = videoRef.current;

    const handleAdEnd = () => {
      console.log("Ad finished. Switching back to skit...");
      setIsAdPlaying(false); // Switch back to skit
      setCurrentMediaUrl(skit?.mediaUrl || ""); // Set skit URL
      if (video) {
        video.currentTime = savedTime || 0; // Resume skit from saved time
        video
          .play()
          .catch((error) => console.error("Error resuming skit:", error));
      }
    };

    if (video && isAdPlaying) {
      video.addEventListener("ended", handleAdEnd);
    }

    return () => {
      if (video) {
        video.removeEventListener("ended", handleAdEnd);
      }
    };
  }, [isAdPlaying, skit?.mediaUrl, savedTime]);

  useEffect(() => {
    const video = videoRef.current;

    const handleLoadedMetadata = () => {
      if (isAdPlaying) {
        setAdDuration(Math.floor(video.duration)); // Set ad duration dynamically
        console.log("Ad duration set to:", video.duration);
      } else {
        setSkitDuration(Math.floor(video.duration)); // Set skit duration dynamically
        console.log("Skit duration set to:", video.duration);
      }
    };

    if (video) {
      video.addEventListener("loadedmetadata", handleLoadedMetadata);
      return () =>
        video.removeEventListener("loadedmetadata", handleLoadedMetadata);
    }
  }, [isAdPlaying, currentMediaUrl]);

  // Handle play count and switch to ad if needed
  const handlePlayTypeLogic = () => {
    if (!isSkitPlaying && !isAdPlaying) {
      const updatedPlayCount = playCount + 1;
      setPlayCount(updatedPlayCount);
      // Check if it's time to play an ad
      if (updatedPlayCount % 3 === 0) {
        saveSkitTime(skitId, videoRef.current?.currentTime || 0); // Save current skit time
        const nextAd = getNextAd();
        if (nextAd) {
          setCurrentMediaUrl(nextAd.mediaAdUrl); // Set ad URL
          setAdDetails(nextAd); // Store the fetched ad details
          setIsAdPlaying(true); // Start ad playback
          setIsSkitPlaying(false); // Stop skit playback
        }
      }
    }
  };

  const handleVote = useCallback(
    async (skitId, ratings, categoryid, scores) => {
      if (!currentMemberId || !skit) {
        console.error("Missing data: currentMemberId or skit data");
        setRatingError("Missing user or skit data.");
        return;
      }
      setRatingError("");
      try {
        const recordSkitRating = {
          sourceId: currentMemberId, // your current shape
          activity: "rated",
          relatedId: skitId,
          targetId: skit.sourceId,
          ratings,
          categoryid,
          scores,
        };
        const respOrBody =
          await apiRecordSourceIDActivityRatings(recordSkitRating);
        const body =
          respOrBody?.data !== undefined ? respOrBody.data : respOrBody;
        // 🔔 from headers (if axios response)
        const headerCount = Number(
          respOrBody?.headers?.["x-unread-notifications"]
        );
        if (Number.isFinite(headerCount)) {
          notificationsCountFetchedFromMiddleware(headerCount);
        }
        // 🔔 from body (if backend injected)
        if (typeof body?.unreadNotificationCount === "number") {
          notificationsCountFetchedFromMiddleware(body.unreadNotificationCount);
        }
        console.log("Response data check:", body);
        if (body?.success) {
          console.log("New rating average:", body.rating);
          setRating(body.rating);
          setVotes(body.votes);
          // optional; keeps list fresh
          fetchNotifications();
          setShowBallotModal(false);
          localStorage.removeItem("voteScore");
          localStorage.removeItem("ratings");
        } else {
          console.error("Rating update failed:", body?.message);
          setRatingError(body?.message || "Failed to record rating.");
        }
      } catch (error) {
        // Even on error, hydrate if server sent unread
        const errHeaderCount = Number(
          error?.response?.headers?.["x-unread-notifications"]
        );
        if (Number.isFinite(errHeaderCount)) {
          notificationsCountFetchedFromMiddleware(errHeaderCount);
        }
        const errBody = error?.response?.data;
        if (typeof errBody?.unreadNotificationCount === "number") {
          notificationsCountFetchedFromMiddleware(
            errBody.unreadNotificationCount
          );
        }
        console.error("Error updating rating count:", error);
        setRatingError("You can only vote once per skit.");
      }
    },
    [currentMemberId, skit]
  );

  const handleClap = useCallback(async () => {
    if (!currentMemberId || !skit) {
      console.error("Missing data: currentMemberId or skit");
      return;
    }
    try {
      // Optimistically update the local state
      setClaps((prev) => prev + 1);
      const responseData = await apiRecordSourceIDActivityClap(
        currentMemberId,
        "clapped",
        skit.id,
        skit.sourceId
      );
      // suspension eject (your pattern)
      if (isSuspendedFrom(responseData)) {
        await handleLogout();
        setClaps((prev) => Math.max(0, prev - 1)); // revert optimistic
        return;
      }
      // 🔔 hydrate unread badge if backend included a count
      if (typeof responseData?.unreadNotificationCount === "number") {
        notificationsCountFetchedFromMiddleware(
          responseData.unreadNotificationCount
        );
      }
      console.log("Response data check:", responseData);
      if (responseData.type === "skitClapUpdated") {
        console.log(
          "Backend clap count updated successfully, new claps:",
          responseData.claps
        );
        setClaps(responseData.claps);
        // Optionally update the list of skits if necessary
      } else if (responseData.type === "activityLogged") {
        console.log("Clap already counted, current claps:", responseData.claps);
        setClaps(responseData.claps);
      }
    } catch (error) {
      // suspension on error path too
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout();
        setClaps((prev) => Math.max(0, prev - 1)); // revert optimistic
        return;
      }
      // 🔔 hydrate badge from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      console.error("Error updating clap count:", error);
      setClapsError("Failed to update clap count due to server error.");
      // Revert optimistic update in case of error
      setClaps((prev) => Math.max(0, prev - 1));
    }
  }, [
    currentMemberId,
    skit,
    notificationsCountFetchedFromMiddleware,
    handleLogout,
  ]);

  const handleVolumeChange = (newVolume) => {
    // console.log("Volume Change Triggered:", newVolume);
    const video = videoRef.current;
    if (video) {
      video.volume = newVolume / 100; // Convert from 0-100 to 0-1
      console.log("Video Volume Set To:", video.volume);
    }
    setVolume(newVolume); // Update the volume state
  };

  // Inside your component or logic file
  const logView = useCallback(async () => {
    if (!currentMemberId || !skit) {
      console.error("Missing data: currentMemberId or skitData");
      return;
    }
    setViewError(""); // Clear any existing errors
    try {
      const responseData = await apiRecordSourceIDActivityView(
        currentMemberId,
        "viewed",
        skitId,
        skit.sourceId
      );
      if (responseData && responseData.views !== undefined) {
        setViews(responseData.views);
      }
    } catch (error) {
      console.error("Error updating view count:", error);
      setViewError("Failed to update view count due to server error.");
    }
  }, [currentMemberId, skitId, skit, setViews]);

  if (!skit) return <div>Loading...</div>;
  // Handler for vote button click
  const placeVote = async (event) => {
    event.preventDefault(); // Prevent form submit default action
    event.stopPropagation(); // Stop propagation to parent elements
    try {
      const result = await apiFetchUserViewedSkitActivity({
        sourceId: currentMemberId, // Assuming currentMemberId contains the sourceId
        activity: "viewed",
        relatedId: skit.id, // Ensure this is the correct ID
      });

      if (result.success) {
        setShowBallotModal(true); // Open the ballot modal if check is successful
        setViewError(""); // Clear any previous errors if the check is successful
      } else {
        setViewError(
          result.message || "You must watch the full video before voting."
        ); // Set the error message from the server or a default one
      }
    } catch (error) {
      console.error("Error checking voting eligibility:", error);
      let errorMessage = "Error checking voting eligibility."; // Default error message

      if (error.response && error.response.status === 403) {
        errorMessage = "Watching the full video is required to vote."; // Specific error for 403 status
      } else if (error.message) {
        errorMessage = error.message; // Use the error message from the exception if available
      }
      setViewError(errorMessage); // Update the viewError state with the appropriate message
    }
  };
  if (!skit) {
    console.log("Skit is null, rendering loading state.");
    return <div>Loading...</div>;
  }

  const handleProfileThumbnailSlideEnd = (userId) => {
    if (userId) {
      console.log("Navigating to user profile:", userId);
      navigate(`/user-profile/${userId}`);
    } else {
      console.log("User ID is undefined, not navigating.");
      // Handle the undefined case, e.g., show a message or do nothing
    }
  };

  const handleDragUpdate = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time; // Sync video position with drag
    }
  };

  const handleCommentCreated = () => {
    console.log("Comment Created");
  };

  const handleDelete = async (skitId) => {
    try {
      await apiDeleteSkitIdFromSourceIdAction({ skitId });
      console.log("Skit deleted successfully");
      navigate("/skitslist");
      // Scroll to the top after navigation
      setTimeout(() => {
        const element = document.getElementById("skitslist");
        if (element) {
          scrollWithOffset(element, 0);
        }
      }, 0);
    } catch (error) {
      console.error("Error deleting skit:", error);
    }
  };
  const reportSkit = async (choices) => {
    // inline flash so you can keep it local if you want
    const flash = (msg, ms = 1800) => {
      setSuccessFlashMessage?.(msg);
      clearTimeout(reportSkit._t);
      reportSkit._t = setTimeout(() => setSuccessFlashMessage?.(null), ms);
    };
    try {
      const res = await apiReportSkit({
        loggedInAccountHolderId: currentMemberId, // ← keep nomenclature
        skitId,
        reason: JSON.stringify(choices),
      });
      // eject if backend signals suspension
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate unread count (body first, then header)
      const bodyCount = Number(
        res?.data?.unreadNotificationCount ?? res?.unreadNotificationCount
      );
      if (Number.isFinite(bodyCount))
        notificationsCountFetchedFromMiddleware?.(bodyCount);
      const headerCount = Number(res?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount))
        notificationsCountFetchedFromMiddleware?.(headerCount);
      // flash success (no alert)
      flash("Report sent ✅");
      // if suspended, reset or redirect as you like; here we just reload the skit
      const skitStatus = res?.data?.data?.skitStatus ?? res?.data?.skitStatus;
      if (skitStatus === "suspended") {
        const fresh = await apiFetchSkitById({ skitId });
        setSkit?.(fresh?.data);
      }
    } catch (error) {
      // suspension on error path too
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate count from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware?.(errCount);
      }
      flash("Could not submit report. Try again.", 2200);
    } finally {
      setShowReportModal(false);
    }
  };

  const blowWhistle = () => {
    setShowReportModal(true);
  };


  return (
    <div id="skitUrlWrapper">
      <div className="skit-header-row">
        <SolidColorButton
          className="gold-btn custom-style"
          solidColorBtnImage={
            <LeftArrow
              height="15px"
              width="15px"
              fillColor="#964b00"
              strokeWidth="4"
              strokeColor="#964b00"
            />
          }
          solidColorBtnText="Back"
          onClick={() => navigate(-1)}
          solidColorBtnFontWeight="800"
          solidColorBtnFontFamily="helvetica"
          solidColorBtnTextFontSize=".75rem"
          solidColorBtnFontColor="#964b00"
          solidColorBtnWidth="100px"
          solidColorBtnBoxShadow="rgba(0, 0, 0, 0.733) 0px 4px 3px 1px, rgba(0, 0, 0, 0.733) 0px 6px 8px, rgba(0, 0, 0, 0.733) 0px -4px 4px, rgba(0, 0, 0, 0.733) 0px -6px 4px, rgba(0, 0, 0, 0.733) 0px 0px 3px 0px inset"
        />
        {/* <span className="date-posted">
          Posted:{" "}
          {skit.createdAt
            ? new Date(skit.createdAt).toLocaleDateString()
            : "Loading..."}
        </span> */}
        {/* CENTER: Title */}
        <span className="allCaps">
          <TooltipDots text={skit.title || "Loading..."} maxLength={20} />
        </span>
      </div>
      <div id="skitUrlContainer">
        <VideoMonitor
          videoRef={videoRef}
          src={currentMediaUrl}
          autoPlay={isAdPlaying}
          title={!isAdPlaying ? skit?.title || "" : ""} // Show title only for skits
          skitId={!isAdPlaying ? skitId : null}
          onLoadedMetadata={(duration) =>
            console.log("Duration loaded:", duration)
          }
        />
        <div className="under-video-row">
          <div className="under-video-left-side"></div>
          <div className="under-video-middel">
            {isAdPlaying && adDetails?.campaignAdUrl && (
              <AdClickableLink
                url={adDetails.campaignAdUrl} // The ad’s clickable URL
                onClick={() =>
                  apiRecordAdClick(
                    adDetails.adId,
                    currentMemberId,
                    adDetails.targetId,
                    adDetails.campaignName
                  )
                }
                label="Visit Advertiser's Website"
              />
            )}
            {isAdPlaying && (
              <div>
                <TimeRemainingAd
                  videoRef={videoRef}
                  isAdPlaying={isAdPlaying}
                  adDuration={adDuration}
                />
                <div className="sponsored-skitUrl-container">
                  <span className="sponsored-skitUrl-text">Sponsored</span>
                </div>
              </div>
            )}
            {!isAdPlaying && (
              <VideoPlayerControls
                videoRef={videoRef}
                duration={duration}
                playCount={playCount}
                setPlayCount={setPlayCount}
                autoPlay={isAdPlaying}
                onDragUpdate={handleDragUpdate}
                onPlayTypeChange={handlePlayTypeLogic}
                logView={logView}
                onVolumeChange={handleVolumeChange}
                isSkitPlaying={isSkitPlaying}
                setIsSkitPlaying={setIsSkitPlaying}
                savedTime={getSkitTime(skit.id)}
                // updateProgressCallback={(currentTime, totalDuration) => {
                //   console.log(`Progress: ${currentTime}/${totalDuration}`);
                // }}
                updateProgressCallback={() => {}}
              />
            )}
          </div>
          <div classeName="under-video-right-side">
            {viewError && <div className="error-message">{viewError}</div>}
            <div className="viewsCountSkitUrl">
              <span>
                {views} {SingularPlural(views, "View")}
              </span>
            </div>
            <div className="ratingsLinkSkitUrl">
              <HashLink
                to={`/skit/${skit.id}/ratings#top`}
                scroll={(el) => scrollWithOffset(el, 150)}
                style={{ textDecoration: "none" }}
              >
                {ratingError && (
                  <div className="error-message">{ratingError}</div>
                )}
                <div className="rating-link">
                  {votes > 0 ? (
                    <div className="rating-link">
                      <span>
                        {(rating * 5).toFixed(1)} · rating average · {votes}{" "}
                        {SingularPlural(votes, "vote")}
                      </span>
                    </div>
                  ) : null}
                </div>
              </HashLink>
            </div>
            <div id="controlsAndActionsRow">
              <div className="profile-and-clap">
                {/* This is your slider button */}
                {skit.User && (
                  <MemberSlider
                    user={skit.User}
                    slideBtnText="Slide to View Profile"
                    onHandleSlideEnd={handleProfileThumbnailSlideEnd}
                  />
                )}
                {/* Then your clap icon & count */}
                <HandClap onClap={handleClap} claps={claps} />
              </div>
              <div className="skit-actions">
                {showBallotModal && (
                  <BallotModal
                    skitId={skit.id}
                    categoryid={skit.categoryid}
                    onVote={handleVote}
                    onClose={cancelVote}
                  />
                )}
                {hasDeletePermission && (
                  <SmallPlainInsetBtn
                    image={<TrashCanIcon height="20px" width="20px" />}
                    text="Delete"
                    width="88px"
                    onClick={() => handleDelete(skit.id)}
                  />
                )}
                {!denyOtherPermissions && (
                  <>
                    <SmallPlainInsetBtn
                      image={<BallotIcon height="20px" width="20px" />}
                      text="Vote"
                      width="88px"
                      onClick={placeVote}
                    />

                    <SmallPlainInsetBtn
                      image={<WhistleIcon height="20px" width="20px" />}
                      text="Report"
                      width="88px"
                      onClick={blowWhistle}
                    />
                  </>
                )}
                {!hasDeletePermission && (
                  <SmallPlainInsetBtn
                    image={
                      <NotInterestedIcon
                        width="30px"
                        fillColor="var(--color-bloodred)"
                      />
                    }
                    text="Hide"
                    width="88px"
                    style={{ fontSize: "1rem" }}
                    onClick={handleNotInterestedClick}
                  />
                )}
                {notInterestedError && (
                  <div className="error-message">{notInterestedError}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showReportModal && (
        <ReportModal
          onBlowWhistle={reportSkit}
          kind="skit"
          onCancel={() => setShowReportModal(false)}
        />
      )}
      <CommentThreads
        skitId={skit.id}
        onCommentCreated={handleCommentCreated}
      />
    </div>
  );
};
export default SkitURL;
